import React, { useRef, useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import { useSelector, useDispatch } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  updateWorkOrderApi,
  uploadWorkOrderPurchaseOrder,
  uploadWorkOrderExpenseAttachments,
} from "../../../services/api/workOrder";
import { showToast } from "../../../store/toast/toastSlice";
import PayForm from "../../WorkOrder/Forms/PayForm";
import { UserType, WorkOrderStatusType } from "../../../utils/enum";
import useAuth from "../../../hooks/useAuth";
import { blobUrlToFile } from "../../../utils/helpers";
import {
  buildValidationMessage,
  focusFirstInvalidField,
  normalizeValidationResult,
} from "../../../utils/formErrors";
import { isEqual } from "lodash";

const toAmount = (value) => Number(value) || null;

// What Save would actually send — built the same way from the form (redux) and from
// the work order (paymentData), so the button only lights up when pressing it would
// change something. Attachments count by file name: a file just picked has only a
// blob url, so the name is what tells the two apart.
const buildRatesSnapshot = (rates, expenses, purchaseOrders) => ({
  paymentType: rates?.paymentType || "",
  nonBusinessHourRate: toAmount(rates?.nonBusinessHourRate),
  hourlyRate: toAmount(rates?.hourlyRate),
  additionalHourlyRate: toAmount(rates?.additionalHourlyRate),
  minimumHours: toAmount(rates?.minimumHours),
  fixedAmount: toAmount(rates?.fixedAmount),
  halfDayRate: toAmount(rates?.halfDayRate),
  fullDayRate: toAmount(rates?.fullDayRate),
  fthRate: toAmount(rates?.fthRate),
  customHours: toAmount(rates?.customHours),
  customHoursRate: toAmount(rates?.customHoursRate),
  currency: rates?.currency || "",
  purchaseOrderNumber: rates?.purchaseOrderNumber || "",
  expenses: (expenses || [])
    .filter((expense) => expense.expenseType)
    .map((expense) => ({
      id: expense.id,
      expenseType: expense.expenseType,
      description: expense.description || "",
      amount: toAmount(expense.amount),
      attachment: expense.attachment?.fileName || null,
    })),
  purchaseOrders: (purchaseOrders || []).map((purchaseOrder) => ({
    number: purchaseOrder.number || "",
    attachment: purchaseOrder.attachment?.fileName || null,
  })),
});

const EditCustomerRatesModal = ({ open, close, paymentData, poOnly = false }) => {
  const dispatch = useDispatch();
  const payRef = useRef();
  const queryClient = useQueryClient();
  const { authRole } = useAuth();

  const [isSaving, setIsSaving] = useState(false);
  const id = useSelector((state) => state.workOrder.workOrderDetails.id);
  const payment = useSelector((state) => state.workOrder.payment);
  const currentStatus = useSelector((state) => state.workOrder.workOrderDetails.status);
  const customerStatus = useSelector((state) => state.workOrder.workOrderDetails.customerStatus);
  const hasChanges = !isEqual(
    buildRatesSnapshot(payment?.pay, payment?.expenses, payment?.pay?.purchaseOrders),
    buildRatesSnapshot(paymentData, paymentData?.expenses, paymentData?.purchaseOrders),
  );
  const { isPending: isUpdatingLoading, mutateAsync: updateWorkOrderApiMutation } = useMutation({
    mutationFn: updateWorkOrderApi,
  });
  const {
    isPending: isUploadWorkOrderPurchaseOrderFilesApiLoading,
    error: isUploadWorkOrderPurchaseOrderFilesApiError,
    mutateAsync: uploadWorkOrderPurchaseOrderFileMutation,
  } = useMutation({
    mutationFn: uploadWorkOrderPurchaseOrder,
  });

  const formatWorkOrderExpenses = (expenses) => {
    return expenses
      ?.filter((expense) => expense.expenseType)
      ?.map((expense) => {
        const { expenseType, description, amount, id, expenseKey } = expense;
        return {
          id,
          expenseType,
          description,
          amount: parseFloat(amount),
          expenseKey,
        };
      });
  };
  const isPurchaseOrderChanged = () => {
    const originalPO = paymentData?.purchaseOrders || [];
    const currentPO = payment?.pay?.purchaseOrders || [];

    if (originalPO.length !== currentPO.length) return true;

    for (let i = 0; i < currentPO.length; i++) {
      const curr = currentPO[i];
      const orig = originalPO.find((o) => o.number === curr.number);

      if (!orig) return true;

      const origFile = orig?.attachment?.fileName || null;
      const currFile = curr?.attachment?.fileName || null;

      if (origFile !== currFile) return true;
    }

    return false;
  };

  const handleSubmit = async () => {
    const { valid, errors } = normalizeValidationResult(payRef.current?.isValid?.());

    if (!valid) {
      const message = buildValidationMessage(errors);
      if (message) dispatch(showToast({ message, type: "error" }));
      focusFirstInvalidField();
      return;
    }

    setIsSaving(true);
    try {
      const keyedExpenses = (payment.expenses || []).map((expense) => ({
        ...expense,
        expenseKey: expense.expenseKey || crypto.randomUUID(),
      }));

      // In PO-only mode neither the rates nor the expenses may change, and purchase
      // orders are saved by their own upload call below — so there is nothing to
      // send here.
      const payload = poOnly
        ? null
        : {
            id,
            paymentType: payment.pay.paymentType || "",
            nonBusinessHourRate: Number(payment.pay.nonBusinessHourRate) || null,
            hourlyRate: Number(payment.pay.hourlyRate) || null,
            additionalHourlyRate: Number(payment.pay.additionalHourlyRate) || null,
            minimumHours: Number(payment.pay.minimumHours) || null,
            fixedAmount: Number(payment.pay.fixedAmount) || null,
            halfDayRate: Number(payment.pay.halfDayRate) || null,
            fullDayRate: Number(payment.pay.fullDayRate) || null,
            fthRate: Number(payment.pay.fthRate) || null,
            customHours: Number(payment.pay.customHours) || null,
            customHoursRate: Number(payment.pay.customHoursRate) || null,
            currency: payment.pay.currency || "",
            purchaseOrderNumber: payment.pay.purchaseOrderNumber || "",
            expenses: formatWorkOrderExpenses(keyedExpenses) || [],
          };

      if (payload) await updateWorkOrderApiMutation(payload);

      const followups = [];

      const hasNewPurchaseOrderFile = payment.pay.purchaseOrders.some((po) =>
        po.attachment?.url?.startsWith("blob:"),
      );

      if (isPurchaseOrderChanged() || hasNewPurchaseOrderFile) {
        const formData = new FormData();

        const poData = payment.pay.purchaseOrders.map((po) => {
          const uid = crypto.randomUUID();

          return {
            ...(typeof po.id === "string" ? { id: po.id } : {}),
            number: po.number,
            uid,
          };
        });

        formData.append("purchaseOrders", JSON.stringify(poData));

        for (let i = 0; i < poData.length; i++) {
          const po = poData[i];
          const originalPo = payment.pay.purchaseOrders[i];

          if (originalPo.attachment?.url?.startsWith("blob:")) {
            const fileObj = await blobUrlToFile(
              originalPo.attachment.url,
              originalPo.attachment.fileName,
            );

            const fileWithUID = new File([fileObj], `${po.uid}__-${fileObj.name}`, {
              type: fileObj.type,
            });

            formData.append("files", fileWithUID);
          }
        }

        followups.push(
          uploadWorkOrderPurchaseOrderFileMutation({
            workOrderId: id,
            formData,
          }),
        );
      }

      const expensesWithNewFiles = keyedExpenses.filter((e) =>
        e.attachment?.url?.startsWith("blob:"),
      );

      if (expensesWithNewFiles.length) {
        const expenseFormData = new FormData();
        expenseFormData.append(
          "expenses",
          JSON.stringify(expensesWithNewFiles.map((e) => ({ expenseKey: e.expenseKey }))),
        );

        for (const expense of expensesWithNewFiles) {
          const fileObj = await blobUrlToFile(expense.attachment.url, expense.attachment.fileName);
          const fileWithKey = new File([fileObj], `${expense.expenseKey}__-${fileObj.name}`, {
            type: fileObj.type,
          });
          expenseFormData.append("files", fileWithKey);
        }

        followups.push(
          uploadWorkOrderExpenseAttachments({ workOrderId: id, formData: expenseFormData }),
        );
      }

      if (followups.length) await Promise.all(followups);

      dispatch(
        showToast({
          message: "Updated successfully!",
          type: "success",
        }),
      );
      queryClient.invalidateQueries(["jobDetail", id]);
      close();
    } catch (error) {
      dispatch(
        showToast({
          message: "Failed to update customer rates.",
          type: "error",
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BaseModal open={open} close={close}>
      <div className="py-7 px-12 xl:w-[1400px] lg:w-[1000px] sm:w-[800px]">
        <h6 className="text-dark font-normal border-b pb-1">
          {poOnly
            ? "Edit Purchase Orders"
            : authRole === UserType.CUSTOMER
              ? "Edit Payment"
              : "Edit Customer Rates"}
        </h6>

        <div className="mb-2 mt-3">
          <PayForm
            ref={payRef}
            isEditMode={true}
            paymentData={paymentData}
            currentStatus={currentStatus}
            customerStatus={customerStatus}
            disableRateFields={poOnly}
            disableExpenses={poOnly}
            queryClient={queryClient}
          />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          isLoading={isSaving}
          loaderSize={24}
          loaderColor="primary"
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || isSaving}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditCustomerRatesModal;
